import React, { useState, useEffect, useContext, Fragment } from 'react';
import { Card, CardBody, Row, Col, CustomInput, FormGroup, Button, Spinner } from 'reactstrap';
import PageHeader from '../../components/common/PageHeader';
import { EnvironmentContext } from '../../context/Context';
import { informesServices } from '../../services';
import WizardInput from '../../components/auth/wizard/WizardInput';
import DetailRow from '../../components/GestionPersonaEmpresa/personDetails/DetailRow';
import ButtonIcon from '../../components/common/ButtonIcon';
import cogoToast from 'cogo-toast';
import XLSX from 'sheetjs-style';
import { numeroFormat, valorFormat } from '../../helpers/formatters';

// tipo: 'valor' = pesos con total, 'numero' = cifra sin total, 'fecha' = AAAA-MM-DD
const COLUMNAS = [
    { titulo: 'Crédito', campo: 'credito', ancho: 10 },
    { titulo: 'Fecha Desembolso', campo: 'fecha', ancho: 16, tipo: 'fecha' },
    { titulo: 'Identificación', campo: 'identificacion', ancho: 16 },
    { titulo: 'Cliente', campo: 'nomcliente', ancho: 38 },
    { titulo: 'Línea', campo: 'nomlinea', ancho: 22 },
    { titulo: 'Sublínea', campo: 'nomsublinea', ancho: 22 },
    { titulo: 'Monto', campo: 'monto', ancho: 16, tipo: 'valor' },
    { titulo: 'Plazo', campo: 'plazo', ancho: 8, tipo: 'numero' },
    { titulo: 'Tasa %', campo: 'tasa', ancho: 8, tipo: 'numero' },
    { titulo: 'Valor Cuota', campo: 'valorCuota', ancho: 16, tipo: 'valor' },
    { titulo: 'Saldo Capital', campo: 'saldoCapital', ancho: 16, tipo: 'valor' },
    { titulo: 'Interés Vencido', campo: 'interesVencido', ancho: 16, tipo: 'valor' },
    { titulo: 'Saldo Total', campo: 'saldo', ancho: 16, tipo: 'valor' },
    { titulo: 'Vencimiento', campo: 'fechaVencimiento', ancho: 14, tipo: 'fecha' },
    { titulo: 'Asesor', campo: 'nomasesor', ancho: 32 },
];

const FORMATO_VALOR = '#,##0.00';

const fechaCorta = s => (s ? s.substring(0, 10).split('-').reverse().join('/') : '');

const InfCreditosActivos = () => {
    const environment = useContext(EnvironmentContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listPagadurias, setListPagadurias] = useState([]);
    const [pagaduriaSelected, setPagaduriaSelected] = useState(null);
    const [corte, setCorte] = useState('');

    useEffect(() => {
        if (!environment || !environment.pagadurias) return;
        const list = environment.pagadurias.map(p => ({
            value: p.id,
            label: p.nombreComercial || p.razonSocial,
        }));
        setListPagadurias(list);
    }, [environment]);

    const nombrePagaduria = pagaduriaSelected
        ? pagaduriaSelected.nombreComercial || pagaduriaSelected.razonSocial
        : '';

    const onChangePagaduria = (e) => {
        const val = e.target.value;
        setData([]);
        if (!val) { setPagaduriaSelected(null); return; }
        const found = environment.pagadurias.find(p => String(p.id) === String(val));
        setPagaduriaSelected(found || null);
    };

    const onSubmit = async () => {
        if (!pagaduriaSelected) {
            cogoToast.warn('Por favor seleccionar la pagaduría', { position: 'bottom-left', heading: 'Campos requeridos' });
            return;
        }
        setLoading(true);
        const response = await informesServices.getCreditosActivos({ idPagaduria: pagaduriaSelected.id });
        setLoading(false);
        if (!response || response.error) {
            cogoToast.error(response?.msg || "No se logró completar la operación", { position: 'bottom-left', heading: 'Error al cargar los datos', hideAfter: 6 });
            return;
        }
        setCorte(new Date().toLocaleDateString('es-CO'));
        setData(response.objResponse);
        if (!response.objResponse[0]) {
            cogoToast.info('La pagaduría no tiene créditos activos', { position: 'bottom-left' });
        }
    };

    const totales = COLUMNAS.reduce((acc, c) => {
        if (c.tipo === 'valor') acc[c.campo] = data.reduce((s, i) => s + (i[c.campo] || 0), 0);
        return acc;
    }, {});

    const exportar = () => {
        const n = COLUMNAS.length;
        const encabezado = [
            ['INFORME DE CRÉDITOS ACTIVOS'],
            [`Pagaduría: ${nombrePagaduria}`],
            [`Con corte al ${corte}`],
            COLUMNAS.map(c => c.titulo),
        ];
        const filas = data.map(i => COLUMNAS.map(c => {
            const v = i[c.campo];
            if (c.tipo === 'fecha') return fechaCorta(v);
            return v === null || v === undefined ? '' : v;
        }));
        const filaTotal = COLUMNAS.map((c, idx) =>
            idx === 0 ? `TOTAL (${data.length} créditos)` : c.tipo === 'valor' ? totales[c.campo] : '');

        const ws = XLSX.utils.aoa_to_sheet([...encabezado, ...filas, filaTotal]);
        ws['!cols'] = COLUMNAS.map(c => ({ wch: c.ancho }));
        ws['!merges'] = [0, 1, 2].map(r => ({ s: { r, c: 0 }, e: { r, c: n - 1 } }));

        const primeraFila = encabezado.length;
        const ultimaFila = primeraFila + filas.length;
        for (let R = 0; R <= ultimaFila; R++) {
            const negrilla = R < primeraFila || R === ultimaFila;
            COLUMNAS.forEach((c, C) => {
                const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
                if (!cell) return;
                const s = {};
                if (negrilla) s.font = { bold: true };
                if (R < 3) s.alignment = { horizontal: 'center' };
                if (R >= primeraFila && c.tipo === 'valor' && cell.t === 'n') {
                    cell.z = FORMATO_VALOR;
                    s.numFmt = FORMATO_VALOR;
                }
                if (Object.keys(s).length) cell.s = s;
            });
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Créditos activos');
        const archivo = nombrePagaduria.trim().replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
        XLSX.writeFile(wb, `creditos-activos-${archivo}-${new Date().toISOString().substring(0, 10)}.xlsx`);
    };

    const celda = (i, c) => {
        const v = i[c.campo];
        if (c.tipo === 'fecha') return fechaCorta(v);
        if (c.tipo === 'valor') return valorFormat(v);
        if (c.tipo === 'numero') return numeroFormat(v);
        return v;
    };

    const alinear = c => ({ textAlign: c.tipo === 'valor' || c.tipo === 'numero' ? 'right' : 'left' });

    return (
        <Fragment>
            <PageHeader title="Informe de Créditos Activos" className="mb-3" />

            <Card className="my-3">
                <CardBody className="px-4">
                    <Row>
                        <Col lg={4} className="mx-2">
                            <DetailRow title="Pagaduría">
                                <FormGroup>
                                    <WizardInput
                                        type="select"
                                        tag={CustomInput}
                                        value={pagaduriaSelected ? pagaduriaSelected.id : ''}
                                        placeholder="Seleccionar"
                                        id="pagaduriaCreditosActivos"
                                        name="pagaduria"
                                        options={listPagadurias}
                                        onChange={onChangePagaduria}
                                        errors={[]}
                                    />
                                </FormGroup>
                            </DetailRow>
                        </Col>
                        <Col lg={2} className="mx-2">
                            <DetailRow title="-">
                                <Button onClick={onSubmit} color="primary" disabled={loading}>
                                    Buscar
                                </Button>
                            </DetailRow>
                        </Col>
                        <Col lg={2} className="mx-2">
                            <DetailRow title="-" titleClass="text-white">
                                <ButtonIcon
                                    onClick={exportar}
                                    icon="file-excel"
                                    transform="shrink-3 down-2"
                                    color="falcon-default"
                                    size="sm"
                                    disabled={!data || !data[0]}
                                >
                                    Exportar
                                </ButtonIcon>
                            </DetailRow>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading && (
                <Row className="py-4 d-flex justify-content-center">
                    <Spinner color="primary" />
                </Row>
            )}

            {!loading && data && data[0] && (
                <Card className="my-3 table-responsive">
                    <table className="table table-dashboard table-striped table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'center' }} colSpan={COLUMNAS.length}>INFORME DE CRÉDITOS ACTIVOS</th>
                            </tr>
                            <tr>
                                <th style={{ textAlign: 'center' }} colSpan={COLUMNAS.length}>Pagaduría: {nombrePagaduria}</th>
                            </tr>
                            <tr>
                                <th style={{ textAlign: 'center' }} colSpan={COLUMNAS.length}>Con corte al {corte}</th>
                            </tr>
                            <tr>
                                {COLUMNAS.map(c => <th key={c.campo} style={alinear(c)}>{c.titulo}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(i => (
                                <tr key={i.credito}>
                                    {COLUMNAS.map(c => <td key={c.campo} style={alinear(c)}>{celda(i, c)}</td>)}
                                </tr>
                            ))}
                            <tr>
                                {COLUMNAS.map((c, idx) => (
                                    <td key={c.campo} style={{ ...alinear(c), fontWeight: 'bold' }}>
                                        {idx === 0 ? `TOTAL (${data.length} créditos)` : c.tipo === 'valor' ? valorFormat(totales[c.campo]) : ''}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </Card>
            )}
        </Fragment>
    );
};

export default InfCreditosActivos;
